// Modo viagem (0.23.0, pedido do titular em 12/10/2026): na viagem em
// andamento, o escritório e o motorista veem o próximo passo (iniciar,
// cheguei para carregar, carregado, cheguei para descarregar, carga
// entregue, encerrar), a rota, os horários, o abastecimento planejado, o
// pedágio, a manutenção, os documentos e os gastos perto do previsto. No
// fim, o previsto e o realizado, com as principais causas da diferença.
// Sem sinal, os botões funcionam: os eventos ficam na fila do aparelho.
import {
  anexos, cargaEntregue, categorias, colaboradores, despesas, entregas, eventosDaViagem, fretes, lancamentosDaViagem, lugaresDaViagem,
  manutencao, paradaEntregue, raioxDaEmpresa, regras, veiculos,
} from '../dados.js';
import { creditosDaViagem } from '../lib/acerto.js';
import { h, trocar } from '../lib/dom.js';
import { cargasEmOrdem, lugar, paradasDaCarga, textoDoMomento } from '../lib/entregas.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, lerInteiro, lerNumero, numeroBR, plural } from '../lib/formato.js';
import { kmAtual, planoDoVeiculo, textoDaSituacao as textoDaManutencao } from '../lib/manutencao.js';
import {
  MODO_VIAGEM, REGRAS_DE_DESCANSO, TIPOS_DE_PARADA, abastecimentosPlanejados, compararComPrevisto, documentosDaCarga, eventosEmOrdem, fimDaViagem,
  horariosPrevistos, inicioDaViagem, kmPelaEstrada, manutencaoNaViagem, motoristaDaViagem, nomeDoDocumento, passagem, planoDaViagem,
  planoDasCargas, realizadoDaViagem, ritmoDaEmpresa, sinalDaCausa, textoDeHoras, vencimentos,
} from '../lib/modo-viagem.js';
import { custoDosNumeros } from '../lib/raiox.js';
import { estado as sinal } from '../local/sincronia.js';
import { ehMotorista, hojeDaEmpresa, motoristaGrava, podeGravar, sessao } from '../sessao.js';
import { avisar, carregando, confirmar, dialogo, faixa, secao } from '../ui/componentes.js';
import { botoesDeFoto, miniaturaDoAnexo } from '../ui/fotos.js';

const gravaAqui = () => (ehMotorista() ? motoristaGrava() : podeGravar());
const fuso = () => sessao.empresa?.fuso_horario;
const momento = (quando) => textoDoMomento(quando, { fuso: fuso() });
const km = (n) => `${numeroBR(Math.round(n))} km`;
const litrosTexto = (n) => `${numeroBR(Math.round(n))} litros`;
const diasTexto = (n) => `${String(Math.round(n * 10) / 10).replace('.', ',')} ${Math.round(n * 10) / 10 === 1 ? 'dia' : 'dias'}`;
const base = () => (sessao.empresa?.cidade && sessao.empresa?.uf ? { cidade: sessao.empresa.cidade, uf: sessao.empresa.uf } : null);
// Os links de lançar despesa do app de quem está usando.
const novaDespesa = (viagem, extra = '') => (ehMotorista()
  ? `#/m/despesas/nova?viagem=${viagem.id}${extra}`
  : `#/despesas/nova?viagem=${viagem.id}${extra}&volta=/viagens/${viagem.id}`);

// ---------------------------------------------------------------------
// Os dados
// ---------------------------------------------------------------------
export async function dadosDoModoViagem(viagem) {
  const [cargas, todasParadas, eventos, gastos, cats, frota, motorista, todosAnexos, itens, leituras, listaRegras, lancamentos] = await Promise.all([
    fretes.daViagem(viagem.id), entregas.listar(), eventosDaViagem.daViagem(viagem.id), despesas.daViagem(viagem.id), categorias.todas(),
    veiculos.listar(), colaboradores.obter(viagem.motorista_id), anexos.todos(), manutencao.doVeiculo(viagem.veiculo_id), despesas.leituras(),
    regras.listar(), lancamentosDaViagem(viagem.id),
  ]);
  const caminhao = frota.find((v) => v.id === viagem.veiculo_id) ?? null;
  const ids = new Set(cargas.map((f) => f.id));
  return {
    viagem,
    cargas,
    paradas: todasParadas.filter((p) => ids.has(p.frete_id)),
    eventos,
    gastos,
    categorias: cats,
    caminhao,
    motorista,
    anexos: todosAnexos.filter((a) => a.frete_id && ids.has(a.frete_id)),
    itens,
    leituras,
    regras: listaRegras,
    lancamentos,
  };
}

// O realizado e a comparação com o previsto (null sem o previsto).
export function contasDaViagem(d, { agora = new Date().toISOString() } = {}) {
  const plano = planoDaViagem(d.eventos);
  const motorista = motoristaDaViagem({ viagem: d.viagem, cargas: d.cargas, regras: d.regras, lancamentos: d.lancamentos, creditosDaViagem });
  const real = realizadoDaViagem({
    viagem: d.viagem, cargas: d.cargas, despesas: d.gastos, categorias: d.categorias, eventos: d.eventos, motorista, plano, agora,
  });
  return { plano, real, comparacao: plano ? compararComPrevisto(plano, real) : null };
}

// ---------------------------------------------------------------------
// O previsto
// ---------------------------------------------------------------------
// Os números do Raio-X da empresa: no escritório, as tabelas do aparelho;
// no app do motorista, os que o escritório guardou no servidor.
async function numerosDaEmpresa() {
  if (ehMotorista()) {
    const linha = await raioxDaEmpresa.ler();
    return { numeros: linha?.numeros ?? null, reserva: Number(linha?.reserva_km) || 0 };
  }
  const { fonteDoEscritorio } = await import('./raiox.js');
  const fonte = await fonteDoEscritorio();
  return { numeros: await fonte.numeros(), reserva: Number(fonte.preferencias.reserva) || 0 };
}

// As coordenadas das cidades (para os km de cada trecho e os pontos de
// abastecimento) e as cidades-polo. Sem internet, null.
async function lugaresDosTrechos(nomes) {
  if (!sinal.online || nomes.length === 0) return null;
  const unicos = [...new Set(nomes.filter(Boolean))].slice(0, 40);
  const pares = unicos.map((n) => {
    const i = n.lastIndexOf('/');
    return { nome: n, cidade: i > 0 ? n.slice(0, i) : n, uf: i > 0 ? n.slice(i + 1) : '' };
  });
  try {
    const r = await lugaresDaViagem(pares);
    const porNome = new Map(pares.map((p, i) => [p.nome, r?.cidades?.[i] ?? null]));
    return { porNome, polos: r?.polos ?? [] };
  } catch (erro) {
    console.warn(`Modo viagem: lugares: ${erro?.message ?? erro}`);
    return null;
  }
}

// Os pontos de abastecimento do previsto (com o tanque do caminhão).
export async function comAbastecimentos(plano, { tanque }) {
  if (!plano || !(Number(tanque) > 0)) return plano;
  const lugares = await lugaresDosTrechos(plano.trechos.flatMap((t) => [t.de, t.para]));
  if (!lugares) return { ...plano, tanque: Number(tanque) };
  const trechos = plano.trechos.map((t) => ({ km: t.km, de: lugares.porNome.get(t.de), para: lugares.porNome.get(t.para) }));
  return {
    ...plano,
    tanque: Number(tanque),
    abastecimentos: abastecimentosPlanejados({ trechos, consumo: plano.numeros.consumo, tanque, preco: plano.numeros.preco, polos: lugares.polos }),
  };
}

// Faz o previsto pelas cargas da viagem e guarda (evento "plano"). Lança erro
// com o motivo quando não dá (sem carga, sem o consumo, sem internet).
export async function fazerPrevisto(d) {
  if (!d.cargas.some((f) => !f.deleted_at && !f.complemento_de)) throw new Error('SEM_CARGA: lance a carga da viagem antes de fazer o previsto.');
  if (!sinal.online) throw new Error('SEM_INTERNET: o previsto precisa de internet (para a distância e os números da empresa).');
  const { numeros, reserva } = await numerosDaEmpresa();
  const custo = custoDosNumeros(numeros, { veiculoId: d.viagem.veiculo_id, reserva });
  if (!custo.porKm) throw new Error('SEM_CONSUMO: ainda sem o consumo do caminhão. Lance os abastecimentos com o km do painel (ou faça um Raio-X digitando o consumo).');
  const nomes = [];
  const casa = base();
  if (casa) nomes.push(lugar(casa.cidade, casa.uf));
  for (const f of d.cargas) nomes.push(lugar(f.origem_cidade, f.origem_uf), lugar(f.destino_cidade, f.destino_uf));
  const lugares = await lugaresDosTrechos(nomes);
  const coordenadas = (l) => lugares?.porNome.get(lugar(l.cidade, l.uf)) ?? null;
  let plano = planoDasCargas({
    cargas: d.cargas, custo, ritmo: ritmoDaEmpresa(numeros?.aprendido), base: casa,
    kmEntreCidades: (a, b) => kmPelaEstrada(coordenadas(a), coordenadas(b)), feitoEm: new Date().toISOString(),
  });
  if (!plano) throw new Error('SEM_CARGA: lance a carga da viagem antes de fazer o previsto.');
  plano = await comAbastecimentos(plano, { tanque: d.caminhao?.tanque_litros });
  await eventosDaViagem.criar({ viagem_id: d.viagem.id, tipo: 'plano', momento: new Date().toISOString(), dados: { plano } }, { esperarServidor: false });
  return plano;
}

// ---------------------------------------------------------------------
// O próximo passo
// ---------------------------------------------------------------------
const entregue = (f) => Boolean(f.entregue_em || f.data_descarga);
const deOnde = (f) => lugar(f.origem_cidade, f.origem_uf) || 'a origem';
const paraOnde = (f) => lugar(f.destino_cidade, f.destino_uf) || 'o destino';

// O que falta fazer agora: { tipo, frete?, parada?, desde? }.
export function proximoPasso(d) {
  const inicio = inicioDaViagem(d.eventos);
  const fim = fimDaViagem(d.eventos);
  if (!inicio && d.viagem.status === 'aberta') return { tipo: 'iniciar' };
  if (fim || d.viagem.status !== 'aberta') return { tipo: 'encerrada' };
  for (const f of cargasEmOrdem(d.cargas).filter((x) => !x.complemento_de && !entregue(x))) {
    const carregar = passagem(d.eventos, f.id, 'carregar');
    if (!carregar.saida) return carregar.chegada ? { tipo: 'carregando', frete: f, desde: carregar.chegada.momento } : { tipo: 'ir_carregar', frete: f };
    const parada = paradasDaCarga(d.paradas, f.id).find((p) => !p.data_entrega);
    if (parada) return { tipo: 'parada', frete: f, parada };
    const descarregar = passagem(d.eventos, f.id, 'descarregar', f);
    return descarregar.chegada ? { tipo: 'descarregando', frete: f, desde: descarregar.chegada.momento } : { tipo: 'ir_descarregar', frete: f };
  }
  return { tipo: 'voltar' };
}

async function registrar(d, dados, { aoMudar, sucesso }) {
  try {
    await eventosDaViagem.criar({ viagem_id: d.viagem.id, momento: new Date().toISOString(), ...dados }, { esperarServidor: false });
    if (sucesso) avisar(sucesso);
    aoMudar();
  } catch (erro) {
    avisar(mensagemDeErro(erro), 'erro');
  }
}

function campoDeKm(id, rotulo, valor = null) {
  const entrada = h('input', { type: 'text', id, inputmode: 'numeric', value: valor !== null && valor !== undefined ? numeroBR(valor) : '' });
  entrada.addEventListener('input', () => {
    const digitos = entrada.value.replace(/\D/g, '').slice(0, 7);
    entrada.value = digitos.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  });
  return { entrada, bloco: h('div', { class: 'campo' }, h('label', { for: id }, rotulo), entrada) };
}

function dialogoIniciar(kmSugerido) {
  return dialogo((fechar) => {
    const kmCampo = campoDeKm('iniciar-km', 'Km no painel (opcional)', kmSugerido);
    return [
      h('h2', null, 'Iniciar viagem'),
      h('p', null, 'O app passa a acompanhar a viagem: os horários, os gastos perto do previsto e, no fim, o previsto e o realizado.'),
      kmCampo.bloco,
      h('div', { class: 'acoes' },
        h('button', { class: 'botao cheio', type: 'button', onClick: () => fechar({ km: lerInteiro(kmCampo.entrada.value) }) }, 'Iniciar viagem'),
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(null) }, 'Cancelar')),
    ];
  });
}

function dialogoParada() {
  return dialogo((fechar) => {
    let escolhido = null;
    const erro = h('p', { class: 'erro-campo', role: 'alert' });
    const opcoes = TIPOS_DE_PARADA.map(([valor, nome]) => h('label', { class: 'opcao-parada' },
      h('input', { type: 'radio', name: 'motivo-parada', value: valor, onChange: () => { escolhido = valor; } }), h('span', null, nome)));
    return [
      h('h2', null, 'Registrar parada'),
      h('fieldset', { class: 'campo' }, h('legend', null, 'Parou para quê?'), h('div', { class: 'opcoes-parada' }, opcoes)),
      erro,
      h('div', { class: 'acoes' },
        h('button', {
          class: 'botao cheio',
          type: 'button',
          onClick: () => {
            if (!escolhido) {
              erro.textContent = 'Escolha o motivo da parada.';
              return;
            }
            fechar(escolhido);
          },
        }, 'Registrar'),
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(null) }, 'Cancelar')),
    ];
  });
}

function dialogoEncerrarNoModoViagem(kmSugerido) {
  return dialogo((fechar) => {
    const kmCampo = campoDeKm('fim-km', 'Km no painel (opcional)', kmSugerido);
    const tag = h('input', { type: 'text', id: 'fim-tag', inputmode: 'decimal', placeholder: 'R$ 0,00' });
    const erro = h('p', { class: 'erro-campo', role: 'alert' });
    return [
      h('h2', null, 'Encerrar viagem'),
      h('p', null, 'Chegou à base? O app compara o previsto com o realizado. O escritório confirma o encerramento no app dele.'),
      kmCampo.bloco,
      h('div', { class: 'campo' }, h('label', { for: 'fim-tag' }, 'Pedágio pago na tag nesta viagem (opcional)'), tag,
        h('p', { class: 'dica' }, 'Só se o pedágio não foi lançado como despesa: a fatura da tag chega depois, no mês.')),
      erro,
      h('div', { class: 'acoes' },
        h('button', {
          class: 'botao cheio',
          type: 'button',
          onClick: () => {
            const valorTag = lerNumero(tag.value);
            if (valorTag !== null && !(valorTag >= 0 && valorTag < 100000)) {
              erro.textContent = 'Confira o valor do pedágio.';
              return;
            }
            fechar({ km: lerInteiro(kmCampo.entrada.value), pedagioTag: valorTag });
          },
        }, 'Encerrar viagem'),
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(null) }, 'Cancelar')),
    ];
  });
}

function blocoDoProximoPasso(d, passo, { aoMudar, contas }) {
  const grava = gravaAqui();
  const botao = (texto, onClick, classe = 'botao cheio') => grava && h('button', { class: classe, type: 'button', onClick }, texto);
  const doFrete = (f, tipo, motivo, sucesso) => () => registrar(d, { tipo, motivo, frete_id: f.id }, { aoMudar, sucesso });
  const kmSugerido = kmAtual(d.caminhao, d.leituras, d.itens)?.km ?? d.viagem.km_saida ?? null;
  let titulo;
  let linhas = [];
  let acoes = [];

  if (passo.tipo === 'iniciar') {
    titulo = 'Iniciar o modo viagem';
    linhas = ['Toque em "Iniciar viagem" quando o caminhão sair: o app acompanha a viagem e, no fim, compara o previsto com o realizado.'];
    acoes = [botao('Iniciar viagem', async () => {
      const r = await dialogoIniciar(kmSugerido);
      if (!r) return;
      // Sem o previsto, tenta fazer pelas cargas (com internet); se não der, segue sem.
      if (!contas.plano && d.cargas.length > 0) {
        try {
          await fazerPrevisto(d);
        } catch (erro) {
          console.warn(`Modo viagem: previsto ao iniciar: ${erro?.message ?? erro}`);
        }
      }
      registrar(d, { tipo: 'inicio', km: r.km ?? null }, { aoMudar, sucesso: 'Boa viagem! O app está acompanhando.' });
    })];
  } else if (passo.tipo === 'ir_carregar') {
    titulo = `Próximo: carregar em ${deOnde(passo.frete)}`;
    acoes = [
      botao(`Cheguei para carregar em ${deOnde(passo.frete)}`, doFrete(passo.frete, 'chegada', 'carregar', 'Chegada anotada. Quando carregar, toque em "Carregado".')),
      botao('Já está carregado', doFrete(passo.frete, 'saida', 'carregar', 'Anotado: carregado.'), 'botao cheio secundario'),
    ];
  } else if (passo.tipo === 'carregando') {
    titulo = `Carregando em ${deOnde(passo.frete)}`;
    linhas = [`Esperando desde ${momento(passo.desde)} (${textoDeHoras((Date.now() - Date.parse(passo.desde)) / 3600000)}).`];
    acoes = [botao('Carregado: seguir viagem', doFrete(passo.frete, 'saida', 'carregar', 'Boa estrada!'))];
  } else if (passo.tipo === 'parada') {
    const onde = lugar(passo.parada.cidade, passo.parada.uf);
    titulo = `Próxima entrega: ${onde}`;
    acoes = [botao(`Entregue em ${onde}`, async () => {
      if (!(await confirmar({ titulo: `Entregue em ${onde}?`, texto: 'Marca esta entrega no caminho com a data de hoje. A carga continua no caminhão até o destino final.', sim: 'Entregue' }))) return;
      try {
        await paradaEntregue(passo.parada, { hoje: hojeDaEmpresa() });
        avisar('Entrega anotada.');
        aoMudar();
      } catch (erro) {
        avisar(mensagemDeErro(erro), 'erro');
      }
    })];
  } else if (passo.tipo === 'ir_descarregar' || passo.tipo === 'descarregando') {
    const f = passo.frete;
    titulo = passo.tipo === 'ir_descarregar' ? `Próximo: descarregar em ${paraOnde(f)}` : `Descarregando em ${paraOnde(f)}`;
    if (passo.desde) linhas = [`Esperando desde ${momento(passo.desde)} (${textoDeHoras((Date.now() - Date.parse(passo.desde)) / 3600000)}).`];
    const entregar = async () => {
      if (!(await confirmar({ titulo: 'Carga entregue?', texto: `Marca a entrega em ${paraOnde(f)} hoje, agora. O escritório vê na hora.`, sim: 'Carga entregue' }))) return;
      try {
        await cargaEntregue(f, { hoje: hojeDaEmpresa() });
        await eventosDaViagem.criar({ viagem_id: d.viagem.id, frete_id: f.id, tipo: 'saida', motivo: 'descarregar', momento: new Date().toISOString() },
          { esperarServidor: false });
        avisar('Carga entregue!');
        aoMudar();
      } catch (erro) {
        avisar(mensagemDeErro(erro), 'erro');
      }
    };
    acoes = passo.tipo === 'ir_descarregar'
      ? [botao(`Cheguei para descarregar em ${paraOnde(f)}`, doFrete(f, 'chegada', 'descarregar', 'Chegada anotada. Quando entregar, toque em "Carga entregue".')),
        botao('Carga entregue', entregar, 'botao cheio secundario')]
      : [botao('Carga entregue', entregar)];
  } else if (passo.tipo === 'voltar') {
    titulo = 'Todas as cargas entregues';
    linhas = [ehMotorista() ? 'Quando chegar à base, encerre a viagem.' : 'Quando o caminhão voltar à base, encerre a viagem (o motorista também encerra pelo app dele).'];
    if (ehMotorista()) {
      acoes = [botao('Encerrar viagem', async () => {
        const r = await dialogoEncerrarNoModoViagem(kmSugerido);
        if (!r) return;
        registrar(d, { tipo: 'fim', km: r.km ?? null, dados: r.pedagioTag !== null ? { pedagio_tag: r.pedagioTag } : null },
          { aoMudar, sucesso: 'Viagem encerrada. Veja o previsto e o realizado.' });
      })];
    }
  } else {
    return null;
  }
  const emViagem = passo.tipo !== 'iniciar';
  return h('div', { class: 'cartao destaque proximo-passo' }, h('div', { class: 'cartao-corpo' },
    h('span', { class: 'cartao-linha' }, emViagem ? `Em viagem desde ${momento(inicioDaViagem(d.eventos).momento)}` : 'Modo viagem'),
    h('span', { class: 'cartao-titulo' }, titulo),
    linhas.map((l) => h('span', { class: 'cartao-linha' }, l)),
    acoes.filter(Boolean).length > 0 && h('div', { class: 'acoes' }, acoes.filter(Boolean)),
    emViagem && grava && h('button', {
      class: 'botao texto',
      type: 'button',
      onClick: async () => {
        const motivo = await dialogoParada();
        if (motivo) registrar(d, { tipo: 'parada', motivo }, { aoMudar, sucesso: 'Parada anotada.' });
      },
    }, 'Registrar parada')));
}

// ---------------------------------------------------------------------
// Rota, horários, abastecimento, pedágio, manutenção, documentos e gastos
// ---------------------------------------------------------------------
const paraMapa = (texto) => `${texto.replace('/', ', ')}, Brasil`;

function blocoDaRota(d) {
  const lugares = [];
  for (const f of cargasEmOrdem(d.cargas).filter((x) => !x.complemento_de)) {
    const carregar = passagem(d.eventos, f.id, 'carregar');
    lugares.push({ texto: `Carregar em ${deOnde(f)}`, nome: deOnde(f), feito: Boolean(carregar.saida) || entregue(f) });
    for (const p of paradasDaCarga(d.paradas, f.id)) lugares.push({ texto: `Entregar em ${lugar(p.cidade, p.uf)}`, nome: lugar(p.cidade, p.uf), feito: Boolean(p.data_entrega) });
    lugares.push({ texto: `Descarregar em ${paraOnde(f)}`, nome: paraOnde(f), feito: entregue(f) });
  }
  const casa = base();
  if (casa && lugares.length > 0) lugares.push({ texto: `Voltar à base (${lugar(casa.cidade, casa.uf)})`, nome: lugar(casa.cidade, casa.uf), feito: Boolean(fimDaViagem(d.eventos)) });
  if (lugares.length === 0) return null;
  const proximo = lugares.find((l) => !l.feito);
  const pendentes = lugares.filter((l) => !l.feito).map((l) => l.nome);
  const destino = pendentes.at(-1);
  const meio = pendentes.slice(0, -1).slice(0, 9);
  const google = destino && `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(paraMapa(destino))}`
    + `${meio.length ? `&waypoints=${encodeURIComponent(meio.map(paraMapa).join('|'))}` : ''}&travelmode=driving`;
  const waze = proximo && `https://waze.com/ul?q=${encodeURIComponent(paraMapa(proximo.nome))}&navigate=yes`;
  return h('div', { class: 'rota-da-viagem' },
    h('ol', { class: 'lista-simples' }, lugares.map((l) => h('li', { class: l.feito ? 'feito' : l === proximo ? 'proximo' : '' },
      `${l.feito ? '✓ ' : l === proximo ? '→ ' : ''}${l.texto}`))),
    (google || waze) && h('div', { class: 'acoes-lado' },
      google && h('a', { class: 'botao secundario', href: google, target: '_blank', rel: 'noopener' }, 'Abrir a rota no Google Maps'),
      waze && h('a', { class: 'botao secundario', href: waze, target: '_blank', rel: 'noopener' }, `Waze até ${proximo.nome}`)));
}

function detalhe(resumo, ...conteudo) {
  return h('details', { class: 'mais-detalhes' }, h('summary', null, resumo), ...conteudo);
}

function blocoDosHorarios(d, plano) {
  const inicio = inicioDaViagem(d.eventos);
  const linhas = [];
  if (inicio) linhas.push(`Saiu: ${momento(inicio.momento)}${inicio.km ? ` · km ${numeroBR(inicio.km)}` : ''}.`);
  const previstos = inicio && plano ? horariosPrevistos(plano, { inicio: inicio.momento }) : [];
  const porFrete = new Map(d.cargas.map((f) => [f.id, f]));
  for (const p of previstos) {
    const f = p.frete_id ? porFrete.get(p.frete_id) : null;
    const chegou = f ? passagem(d.eventos, f.id, 'descarregar', f).chegada ?? (f.entregue_em ? { momento: f.entregue_em } : null) : null;
    if (chegou) {
      const atraso = (Date.parse(chegou.momento) - Date.parse(p.chegada)) / 3600000;
      linhas.push(`Chegou em ${p.lugar}: ${momento(chegou.momento)} (${Math.abs(atraso) < 1 ? 'na hora prevista' : atraso > 0 ? `${textoDeHoras(atraso)} depois do previsto` : `${textoDeHoras(-atraso)} antes do previsto`}).`);
    } else {
      linhas.push(`${p.motivo === 'vazio' ? 'Chegada prevista em' : 'Previsto descarregar em'} ${p.lugar}: ${momento(p.chegada)}.`);
    }
  }
  for (const e of eventosEmOrdem(d.eventos).filter((x) => x.tipo === 'parada' || x.tipo === 'sos')) {
    const nome = e.tipo === 'sos' ? 'SOS mecânica' : `Parada: ${(TIPOS_DE_PARADA.find(([v]) => v === e.motivo)?.[1] ?? 'outro motivo').toLowerCase()}`;
    linhas.push(`${nome} em ${momento(e.momento)}${e.cidade ? ` (${lugar(e.cidade, e.uf)})` : ''}.`);
  }
  const proxima = previstos.find((p) => !(p.frete_id && entregue(porFrete.get(p.frete_id) ?? {})));
  return detalhe(proxima ? `Horários · ${proxima.motivo === 'vazio' ? 'chegada' : 'descarga'} prevista em ${proxima.lugar}: ${momento(proxima.chegada)}` : 'Horários',
    linhas.length > 0 ? h('ul', { class: 'lista-simples pequeno' }, linhas.map((l) => h('li', null, l)))
      : h('p', { class: 'pequeno' }, 'Os horários aparecem quando a viagem começar ("Iniciar viagem").'),
    plano && h('p', { class: 'pequeno' }, `Ritmo do previsto: ${numeroBR(plano.numeros.kmPorDia)} km por dia${plano.numeros.aprendido ? ' (o das suas últimas viagens)' : ''}, com as paradas.`),
    h('p', { class: 'pequeno' }, h('strong', null, 'Descanso do motorista (lei): ')), h('ul', { class: 'lista-simples pequeno' }, REGRAS_DE_DESCANSO.map((r) => h('li', null, r))));
}

function blocoDoAbastecimento(d, plano, real) {
  const grava = gravaAqui();
  const linhas = [];
  if (plano?.custos?.litros) {
    linhas.push(`Previsto: ${litrosTexto(plano.custos.litros)}, ${dinheiro(plano.custos.diesel)} (${numeroBR(plano.numeros.consumo, { casas: 2 })} km/l a ${dinheiro(plano.numeros.preco)} o litro).`);
  }
  for (const [i, p] of (plano?.abastecimentos ?? []).entries()) {
    linhas.push(`${i + 1}º abastecimento perto do km ${numeroBR(p.km)} da viagem${p.perto_de ? `, por volta de ${p.perto_de}` : ''}: uns ${litrosTexto(p.litros)}${p.valor ? ` (${dinheiro(p.valor)})` : ''}.`);
  }
  const abastecido = real.custos.diesel > 0
    ? `Abastecido até agora: ${real.custos.litros ? `${litrosTexto(real.custos.litros)}, ` : ''}${dinheiro(real.custos.diesel)}${real.custos.litros ? ` (${dinheiro(real.custos.diesel / real.custos.litros)} o litro)` : ''}.`
    : 'Nenhum abastecimento lançado nesta viagem ainda.';
  const semTanque = plano && !(Number(d.caminhao?.tanque_litros) > 0);
  return detalhe(plano?.custos?.litros ? `Abastecimento · previsto ${litrosTexto(plano.custos.litros)}${real.custos.litros ? ` · abastecido ${litrosTexto(real.custos.litros)}` : ''}` : 'Abastecimento',
    linhas.length > 0 && h('ul', { class: 'lista-simples pequeno' }, linhas.map((l) => h('li', null, l))),
    h('p', { class: 'pequeno' }, abastecido),
    semTanque && h('p', { class: 'pequeno' }, 'Ponha quantos litros cabem no tanque no cadastro do caminhão (Veículos): o app passa a dizer onde abastecer.'),
    grava && d.viagem.status === 'aberta' && h('a', { class: 'botao secundario', href: novaDespesa(d.viagem, '&tipo=diesel') }, '+ Diesel'));
}

function blocoDoPedagio(d, plano, real) {
  const grava = gravaAqui();
  const pedagio = d.categorias.find((c) => c.codigo === 'pedagio');
  const fonte = plano?.pedagio?.fonte;
  const previsto = !plano ? null
    : fonte === 'contratante' ? 'o contratante paga (vale-pedágio ou tag dele)'
      : fonte === 'embutido' ? `${dinheiro(plano.pedagio.valor)}, que já vem no frete`
        : fonte === 'digitado' ? `${dinheiro(plano.pedagio.valor)} (o que você disse)`
          : `${dinheiro(plano.pedagio.valor)} (pela média da frota)`;
  return detalhe(`Pedágio${previsto ? ` · previsto ${fonte === 'contratante' ? 'pelo contratante' : dinheiro(plano.pedagio.valor)}` : ''}${real.custos.pedagio > 0 ? ` · pago ${dinheiro(real.custos.pedagio)}` : ''}`,
    previsto && h('p', { class: 'pequeno' }, `Previsto: ${previsto}.`),
    h('p', { class: 'pequeno' }, real.custos.pedagio > 0 ? `Pago até agora: ${dinheiro(real.custos.pedagio)}${real.pedagioNaTag > 0 ? ` (com ${dinheiro(real.pedagioNaTag)} na tag)` : ''}.` : 'Nenhum pedágio lançado nesta viagem ainda.'),
    grava && d.viagem.status === 'aberta' && pedagio && h('a', { class: 'botao secundario', href: novaDespesa(d.viagem, `&categoria=${pedagio.id}`) }, '+ Pedágio'));
}

function blocoDaManutencao(d, plano, real) {
  const hoje = hojeDaEmpresa();
  const plan = d.caminhao ? planoDoVeiculo(d.caminhao, { itens: d.itens, leituras: d.leituras, hoje }) : [];
  const kmFeito = real.km.km ?? 0;
  const kmFaltando = Math.max(0, (plano?.km?.total ?? 0) - kmFeito);
  const diasFaltando = Math.max(0, (plano?.dias ?? 0) - (real.dias.dias ?? 0));
  const naViagem = manutencaoNaViagem(plan, { kmFaltando, diasFaltando });
  const fimPrevisto = (() => {
    const dt = new Date(Date.now() + diasFaltando * 86400000);
    return dt.toISOString().slice(0, 10);
  })();
  const docs = vencimentos({ motorista: d.motorista, caminhao: d.caminhao, hoje, ate: fimPrevisto > hoje ? fimPrevisto : hoje });
  const alerta = naViagem.length > 0 || docs.length > 0;
  return detalhe(`Manutenção e documentos${alerta ? ' · pede atenção' : ''}`,
    naViagem.length > 0
      ? h('ul', { class: 'lista-simples pequeno' }, naViagem.map(({ item, situacao }) => h('li', null, `${item.nome}: ${textoDaManutencao(situacao)}${situacao.estado === 'vencida' ? '' : ' (vence durante a viagem)'}.`)))
      : h('p', { class: 'pequeno' }, plan.length > 0 ? 'Nada do plano de manutenção vence durante a viagem.' : 'O caminhão ainda não tem plano de manutenção (Mais → Manutenção).'),
    docs.length > 0 && h('ul', { class: 'lista-simples pequeno' }, docs.map((x) => h('li', null,
      `${x.nome}: ${x.vencido ? `venceu em ${data(x.data)}` : `vence em ${data(x.data)}`}${x.venceNaViagem ? ' (durante a viagem)' : ''}.`))));
}

function blocoDosDocumentos(d, { aoMudar }) {
  const grava = gravaAqui();
  const principais = cargasEmOrdem(d.cargas);
  if (principais.length === 0) return null;
  let falta = 0;
  let total = 0;
  const porCarga = principais.map((f) => {
    const docs = documentosDaCarga(f, d.anexos);
    total += docs.length;
    falta += docs.filter((x) => !x.ok).length;
    const fotos = docs.flatMap((x) => x.fotos);
    return h('div', { class: 'documentos-da-carga' },
      h('p', null, h('strong', null, `${f.complemento_de ? 'Complemento' : 'Carga'} de ${deOnde(f)} para ${paraOnde(f)}`)),
      h('ul', { class: 'lista-simples pequeno' }, docs.map((x) => h('li', { class: x.ok ? 'feito' : '' },
        `${x.ok ? '✓' : '○'} ${x.nome}${x.codigo === 'cte' && f.cte_numero ? ` nº ${f.cte_numero}` : ''}${x.fotos.length > 0 ? ` (${plural(x.fotos.length, 'foto')})` : ''}`))),
      fotos.length > 0 && h('div', { class: 'fotos' }, fotos.map((a) => miniaturaDoAnexo(a))),
      grava && h('button', { class: 'botao secundario', type: 'button', onClick: () => fotoDeDocumento(d, f, docs, { aoMudar }) }, 'Foto de documento'));
  });
  return detalhe(`Documentos · ${falta === 0 ? 'tudo com foto' : `faltam ${falta} de ${total}`}`, ...porCarga);
}

async function fotoDeDocumento(d, frete, docs, { aoMudar }) {
  const salvo = await dialogo((fechar) => {
    const primeiro = docs.find((x) => !x.ok)?.codigo ?? docs[0]?.codigo ?? 'outro';
    const escolha = h('select', { id: 'documento-tipo' },
      [...docs.map((x) => [x.codigo, x.nome]), ['outro', 'Outro documento']].map(([v, nome]) => h('option', { value: v, selected: v === primeiro }, nome)));
    return [
      h('h2', null, 'Foto de documento'),
      h('div', { class: 'campo' }, h('label', { for: 'documento-tipo' }, 'Qual documento?'), escolha),
      botoesDeFoto(async (preparados) => {
        for (const foto of preparados) {
          await anexos.adicionar({ frete_id: frete.id, dia: hojeDaEmpresa(), documento: escolha.value }, foto);
        }
        fechar(escolha.value);
      }),
      h('div', { class: 'acoes' }, h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(null) }, 'Cancelar')),
    ];
  });
  if (salvo) {
    avisar(`Foto guardada: ${nomeDoDocumento(salvo)}.`);
    aoMudar();
  }
}

// "Diesel: R$ 1.300,00 de R$ 1.920,00 previstos"
function linhaDoGasto(nome, real, previsto) {
  return h('li', null, h('strong', null, `${nome}: `), `${dinheiro(real)}${previsto !== null ? ` de ${dinheiro(previsto)} previstos` : ''}`);
}

function blocoDosGastos(d, plano, real) {
  const grava = gravaAqui();
  const p = plano?.custos ?? null;
  return secao('Gastos da viagem', h('div', { class: 'lista' },
    plano && h('p', { class: 'pequeno' }, 'O que já foi gasto, perto do previsto para a viagem toda.'),
    h('ul', { class: 'lista-simples gastos-da-viagem' },
      linhaDoGasto('Diesel', real.custos.diesel, p?.diesel ?? null),
      linhaDoGasto('Pedágio', real.custos.pedagio, p?.pedagio ?? null),
      linhaDoGasto('Alimentação, pernoite e estacionamento', real.custos.porDia, p?.porDia ?? null),
      linhaDoGasto('Outras da viagem (chapa, balança, Arla…)', real.custos.outras, p?.outras ?? null),
      real.fora > 0 && h('li', null, h('strong', null, 'Fora da conta (manutenção, multa…): '), dinheiro(real.fora))),
    grava && d.viagem.status === 'aberta' && h('div', { class: 'acoes-lado' },
      h('a', { class: 'botao secundario', href: novaDespesa(d.viagem, '&tipo=diesel') }, '+ Diesel'),
      h('a', { class: 'botao secundario', href: novaDespesa(d.viagem) }, '+ Despesa'))));
}

// ---------------------------------------------------------------------
// O previsto e o realizado
// ---------------------------------------------------------------------
function textoDaCausa(c) {
  const s = sinalDaCausa(c);
  return `${c.nome}: ${s.mais ? '+' : '−'}${dinheiro(s.valor)} (${s.texto}${c.detalhe ? `: ${c.detalhe}` : ''})`;
}

export function blocoPrevistoRealizado({ plano, real, comparacao }, { final = true } = {}) {
  if (!comparacao) return null;
  const diferenca = comparacao.diferenca;
  const tipo = diferenca >= 0 ? 'ok' : Math.abs(diferenca) <= Math.abs(comparacao.previsto) * 0.05 ? 'alerta' : 'perigo';
  const outras = comparacao.causas.filter((c) => !comparacao.principais.includes(c));
  const dias = real.dias.dias;
  return secao(final ? 'Previsto e realizado' : 'Previsto e realizado até agora', h('div', { class: 'lista previsto-realizado' },
    h('dl', { class: 'linha-dados resumo' },
      h('dt', null, 'Previsto'), h('dd', null, dinheiro(comparacao.previsto)),
      h('dt', null, 'Realizado'), h('dd', null, dinheiro(comparacao.realizado)),
      h('dt', null, 'Diferença'), h('dd', { class: diferenca < 0 ? 'perigo' : '' }, `${diferenca < 0 ? '−' : '+'}${dinheiro(Math.abs(diferenca))}`)),
    comparacao.principais.length > 0 && h('div', { class: `faixa ${tipo}` }, h('div', null,
      h('strong', null, 'Principais causas:'),
      h('ul', { class: 'lista-simples' }, comparacao.principais.map((c) => h('li', null, textoDaCausa(c)))))),
    comparacao.causas.length === 0 && faixa('O realizado ficou igual ao previsto.', 'ok'),
    outras.length > 0 && detalhe('Ver as outras diferenças', h('ul', { class: 'lista-simples pequeno' }, outras.map((c) => h('li', null, textoDaCausa(c))))),
    h('ul', { class: 'lista-simples pequeno' },
      h('li', null, `Frete: previsto ${dinheiro(plano.receita)}, realizado ${dinheiro(real.receita)}.`),
      h('li', null, `Km: previsto ${km(plano.km.total)}, ${real.km.km !== null ? `rodado ${km(real.km.km)}` : 'o rodado não foi informado (o km do painel no início e no fim)'}.`),
      dias !== null && dias >= MODO_VIAGEM.diasMinimos
        && h('li', null, `Dias: previsto ${diasTexto(plano.dias)}, ${real.dias.emAndamento ? 'até agora' : 'realizado'} ${diasTexto(dias)}${real.dias.fonte === 'datas' ? ' (pelas datas)' : ''}.`),
      comparacao.lucroPorDia.previsto !== null && comparacao.lucroPorDia.realizado !== null && !real.dias.emAndamento
        && h('li', null, `Sobra por dia: prevista ${dinheiro(comparacao.lucroPorDia.previsto)}, realizada ${dinheiro(comparacao.lucroPorDia.realizado)}.`),
      real.fora > 0 && h('li', null, `Fora da conta: ${dinheiro(real.fora)} de manutenção e outros gastos (o previsto usa a reserva por km).`),
      h('li', null, `Previsto feito em ${data(String(plano.feito_em ?? '').slice(0, 10)) || '—'}${plano.fonte === 'raiox' ? ', no Raio-X da carga' : ', pelas cargas da viagem'}. Os custos fixos não entram (saem da sobra).`)),
    final && h('p', { class: 'pequeno' }, 'Cada viagem encerrada com o "Iniciar" e o "Encerrar" ensina o app: o ritmo e a espera de verdade entram no Planejar o trajeto e no previsto das próximas viagens.')));
}

// ---------------------------------------------------------------------
// O bloco inteiro
// ---------------------------------------------------------------------
// O modo viagem de uma viagem (escritório e motorista). `aoMudar`: refaz a tela.
export function blocoModoViagem(viagem, { aoMudar }) {
  const lugarDoBloco = h('div', { class: 'modo-viagem' }, carregando('Abrindo o modo viagem…'));
  (async () => {
    const d = await dadosDoModoViagem(viagem);
    trocar(lugarDoBloco, ...montarBlocos(d, { aoMudar }));
  })().catch((erro) => {
    console.error('Modo viagem:', erro);
    trocar(lugarDoBloco, faixa(mensagemDeErro(erro), 'alerta', 'O modo viagem não abriu'));
  });
  return lugarDoBloco;
}

function montarBlocos(d, { aoMudar }) {
  const contas = contasDaViagem(d);
  const { plano, real, comparacao } = contas;
  const passo = proximoPasso(d);
  const fim = fimDaViagem(d.eventos);
  const aberta = d.viagem.status === 'aberta';
  const grava = gravaAqui();

  // Viagem encerrada (no app ou pelo motorista): o previsto e o realizado primeiro.
  if (!aberta || fim) {
    // O motorista desfaz o encerrar que ele fez (o servidor confere).
    const desfazer = aberta && fim && grava && (!ehMotorista() || !fim.created_by || fim.created_by === sessao.usuario?.id);
    // Viagem antiga, sem nada do modo viagem: nada a mostrar.
    if (!aberta && !plano && d.eventos.length === 0) return [];
    return [
      aberta && fim && faixa(ehMotorista()
        ? `Você encerrou a viagem em ${momento(fim.momento)}. O escritório confirma o encerramento no app dele.`
        : `O motorista encerrou a viagem em ${momento(fim.momento)}${fim.km ? ` (km ${numeroBR(fim.km)})` : ''}. Toque em "Encerrar viagem (voltou à base)" lá embaixo para confirmar.`,
      'info', 'Viagem encerrada'),
      comparacao ? blocoPrevistoRealizado(contas, { final: true })
        : h('p', { class: 'pequeno' }, 'Esta viagem não tem o previsto: o previsto é feito ao aceitar a carga no Raio-X ("Aceitar e iniciar a viagem") ou ao iniciar a viagem com as cargas.'),
      desfazer && h('button', {
        class: 'botao texto',
        type: 'button',
        onClick: async () => {
          try {
            await eventosDaViagem.excluir(fim.id, { esperarServidor: false });
            avisar('Encerramento desfeito: a viagem continua.');
            aoMudar();
          } catch (erro) {
            avisar(mensagemDeErro(erro), 'erro');
          }
        },
      }, 'Desfazer o encerrar (ainda está na estrada)'),
    ];
  }

  return [
    blocoDoProximoPasso(d, passo, { aoMudar, contas }),
    !plano && d.cargas.length > 0 && h('div', { class: 'faixa info' }, h('div', null,
      h('strong', null, 'Sem o previsto'),
      'O previsto da viagem compara, no fim, o que foi planejado com o que aconteceu.',
      grava && h('button', {
        class: 'botao secundario',
        type: 'button',
        onClick: async (ev) => {
          ev.target.disabled = true;
          try {
            await fazerPrevisto(d);
            avisar('Previsto feito.');
            aoMudar();
          } catch (erro) {
            ev.target.disabled = false;
            avisar(mensagemDeErro(erro), 'erro');
          }
        },
      }, 'Fazer o previsto'))),
    plano && h('p', { class: 'pequeno' },
      `Previsto: frete ${dinheiro(plano.receita)} − custos ${dinheiro(plano.custos.total)} = sobra ${dinheiro(plano.resultado)} · ${km(plano.km.total)} · uns ${diasTexto(plano.dias)}.`),
    secao('Rota', blocoDaRota(d) ?? h('p', { class: 'pequeno' }, 'A rota aparece quando a carga for lançada.')),
    h('div', { class: 'lista' },
      blocoDosHorarios(d, plano),
      blocoDoAbastecimento(d, plano, real),
      blocoDoPedagio(d, plano, real),
      blocoDaManutencao(d, plano, real),
      blocoDosDocumentos(d, { aoMudar })),
    ehMotorista() && h('a', { class: 'botao secundario cheio', href: '#/sos' }, 'SOS mecânica'),
    blocoDosGastos(d, plano, real),
  ];
}
